import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { BACKEND_URL } from '@/lib/api';
import { getAnonId, getLeadId, setLeadId } from '@/lib/tracker';

const DISMISS_KEY = 'nc_lead_gate_dismissed';

export default function LeadGate() {
  const [config, setConfig] = useState(null);
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [consentOffers, setConsentOffers] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const shouldShow = useCallback(() => {
    // Never show on checkout/cart pages — protect the conversion funnel
    const path = window.location.pathname;
    if (/^\/(checkout|cart|order-success|login)/.test(path)) return false;
    if (getLeadId()) return false;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const { ts, ttl } = JSON.parse(dismissed);
      if (Date.now() - ts < ttl * 3600000) return false;
    }
    return true;
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;
    const path = window.location.pathname;
    fetch(`${BACKEND_URL}/api/marketing/lead-capture/config?path=${encodeURIComponent(path)}`)
      .then(r => r.json())
      .then(cfg => {
        if (cfg.enabled) {
          setConfig(cfg);
          const delay = cfg.delay_ms || 4000;
          const timer = setTimeout(() => setShow(true), delay);
          return () => clearTimeout(timer);
        }
      })
      .catch(() => {});
  }, [shouldShow]);

  const dismiss = () => {
    setShow(false);
    // Permanent dismiss — never show again for this browser (365 days)
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now(), ttl: 8760 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !mobile.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await fetch(`${BACKEND_URL}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          mobile: mobile.trim(),
          consent_offers: consentOffers,
          consent_marketing: consentOffers,
          utm_source: params.get('utm_source'),
          utm_medium: params.get('utm_medium'),
          utm_campaign: params.get('utm_campaign'),
          page_path: window.location.pathname,
          anon_id: getAnonId(),
        }),
      });
      const data = await res.json();
      if (data.lead_id) {
        setLeadId(data.lead_id);
        setSubmitted(true);
        setTimeout(() => setShow(false), 2000);
      }
    } catch (err) {
      console.error('Lead capture failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !config) return null;

  const copy = config.copy || {};

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" data-testid="lead-gate-overlay">
      <div className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" data-testid="lead-gate-modal">
        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Welcome, {name}!</h3>
            <p className="text-sm text-gray-500 mt-1">You'll receive exclusive offers soon.</p>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#16213e] px-6 py-6 text-white relative">
              <button onClick={dismiss} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20 text-white/70 hover:text-white transition-colors" data-testid="lead-gate-close">
                <X className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold leading-tight" data-testid="lead-gate-headline">
                {copy.headline || 'Get Exclusive Offers'}
              </h2>
              <p className="text-sm text-white/70 mt-1.5 leading-relaxed">
                {copy.subtext || 'Share your details for special deals, saved cart & personalized recommendations.'}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Your Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#FFB800]/20 focus:border-[#FFB800] outline-none"
                  data-testid="lead-gate-name" required />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Mobile Number</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">+91</span>
                  <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder=""
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#FFB800]/20 focus:border-[#FFB800] outline-none"
                    data-testid="lead-gate-mobile" required />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={consentOffers} onChange={e => setConsentOffers(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 mt-0.5" data-testid="lead-gate-consent" />
                <span className="text-xs text-gray-500 leading-relaxed">I agree to receive offers, promotions & marketing communications</span>
              </label>
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-[#FFB800] hover:bg-[#16213e] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="lead-gate-submit">
                {loading ? 'Saving...' : (copy.cta || 'Continue')} <ArrowRight className="w-4 h-4" />
              </button>
              <button type="button" onClick={dismiss} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors" data-testid="lead-gate-skip">
                {copy.skip_text || 'Skip for now'}
              </button>
              <p className="text-[10px] text-gray-400 text-center">
                By continuing, you agree to our Privacy Policy
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
